// Arena text sprites (lane C, backlog C-12; territory-visuals.md §5–§7): names, bag values and
// floating labels in Ubuntu Bold 700 (UFL 1.0, already shipped) with our label style: fill
// #E4E4E4 (or the label's colour), a #33302E copy at (+2, +2) × s px and a 1 px soft stroke.
// Rendered once per (text, size, colour) into a small bitmap and blitted; constant on-screen size
// at every zoom. The cache is cleared once the bold face has loaded (no fallback-face sprites).

import { ARENA_CLIENT, type Rgb } from '@ballio/shared';
import { type Canvas2D, type Ctx2D, paintCanvas } from '../render/canvas.js';
import { css, cssAlpha } from '../render/theme.js';

export const LABEL_FONT = 'Ubuntu, "Trebuchet MS", "Segoe UI", system-ui, sans-serif';
const MAX_SPRITES = 400;

export interface LabelSprite {
  readonly canvas: Canvas2D;
  /** CSS px size of the sprite and where the text baseline sits in it. */
  readonly w: number;
  readonly h: number;
  readonly baseline: number;
  /** Device px per CSS px the sprite was drawn at. */
  readonly k: number;
}

/** The screen scale s = canvas diagonal (CSS px) / 1567 (visuals §0, UI §1). */
export function screenScale(widthPx: number, heightPx: number): number {
  const d = Math.hypot(widthPx, heightPx);
  return d > 0 ? d / 1567 : 1;
}

export class ArenaLabels {
  private readonly cache = new Map<string, LabelSprite | null>();
  private fontAsked = false;

  clear(): void {
    this.cache.clear();
  }

  private askFont(): void {
    if (this.fontAsked) return;
    this.fontAsked = true;
    const fonts = (globalThis as { document?: { fonts?: FontFaceSet } }).document?.fonts;
    if (!fonts || typeof fonts.load !== 'function') return;
    fonts
      .load(`700 24px ${LABEL_FONT}`)
      .then(() => this.cache.clear())
      .catch(() => undefined);
  }

  /** A sprite for `text` at `px` CSS px in colour `fill`, drawn at device scale `k`. */
  sprite(text: string, px: number, fill: Rgb, k: number, shadow = true): LabelSprite | null {
    this.askFont();
    const size = Math.max(6, Math.round(px * 2) / 2);
    const kk = Math.max(1, Math.round(k * 4) / 4);
    const key = `${size}|${fill}|${kk}|${shadow ? 1 : 0}|${text}`;
    const hit = this.cache.get(key);
    if (hit !== undefined) return hit;
    if (this.cache.size >= MAX_SPRITES) this.cache.clear();
    const font = `700 ${size}px ${LABEL_FONT}`;
    const shadowPx = shadow ? Math.max(1, size / 12) : 0;
    let width = text.length * size * 0.62;
    const measured = paintCanvas(1, 1, (ctx) => {
      ctx.font = font;
      width = ctx.measureText(text).width;
    });
    if (!measured) {
      this.cache.set(key, null);
      return null;
    }
    const pad = Math.ceil(size * 0.25 + shadowPx);
    const w = Math.ceil(width + pad * 2);
    const h = Math.ceil(size * 1.35 + pad * 2);
    const baseline = pad + size * 1.0;
    const canvas = paintCanvas(w * kk, h * kk, (ctx: Ctx2D) => {
      ctx.scale(kk, kk);
      ctx.font = font;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.lineJoin = 'round';
      const cx = w / 2;
      if (shadow) {
        ctx.fillStyle = css(ARENA_CLIENT.colors.labelShadow);
        ctx.fillText(text, cx + shadowPx, baseline + shadowPx);
        ctx.strokeStyle = cssAlpha(ARENA_CLIENT.colors.labelShadow, 0.6);
        ctx.lineWidth = 1.2;
        ctx.strokeText(text, cx, baseline);
      }
      ctx.fillStyle = css(fill);
      ctx.fillText(text, cx, baseline);
    });
    const sprite = canvas ? { canvas, w, h, baseline, k: kk } : null;
    this.cache.set(key, sprite);
    return sprite;
  }

  /** Draws `text` centred at (x, baseline y) in CSS px (identity transform × `k` expected). */
  draw(
    ctx: Ctx2D,
    text: string,
    x: number,
    y: number,
    px: number,
    fill: Rgb,
    k: number,
    alpha = 1,
    shadow = true,
  ): void {
    if (text === '' || alpha <= 0) return;
    const sp = this.sprite(text, px, fill, k, shadow);
    if (!sp) return;
    const prev = ctx.globalAlpha;
    if (alpha < 1) ctx.globalAlpha = prev * alpha;
    ctx.drawImage(sp.canvas as CanvasImageSource, x - sp.w / 2, y - sp.baseline, sp.w, sp.h);
    ctx.globalAlpha = prev;
  }
}
